import React, { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Container, Row, Card, CardBody, CardHeader, Collapse, Form } from 'reactstrap';
import TableBuilder, { ApiRequest, ModalButtonBuilder, ButtonBuilder, HoverOver, RegisteredInputsBuilder, VerticalButtonGroup, ToastBuilder, LabelWithSubtext, CheckBox } from '../../../../../../../../../GlobalComponents/Builders';
import { buildOptionsFromArray, buildSelectOptionsFromArray, setInitialFormValues, sortByKey, handleCopy } from '../../../../../../../../../GlobalComponents/Helpers';
import { Btn } from '../../../../../../../../../AbstractElements';
import { Col } from 'react-bootstrap';
import { ProgressLoader } from '../../../../../../../../../Layout/Loader';
import { ArrowRight } from 'react-feather';
import { useForm } from 'react-hook-form';
import moment from 'moment';
import ImportAssociationMappingsModal from './ImportAssociationMappingsModal';
import CompositeValueSelect from '../composite/CompositeValueSelect';

/**
 * AssociationMappingSection — Bidirectional composite chain mapper.
 *
 * Follows the same pattern as ValueMappingSection:
 * - Card with accordion header
 * - TableBuilder with columns
 * - ModalButtonBuilder for Edit
 * - VerticalButtonGroup for actions
 */
const AssociationMappingSection = ({ jobData, jobResourceMappings }) => {

    const [isOpen, setIsOpen] = useState(null);
    const [mappings, setMappings] = useState({});  // keyed by sourceResourceId
    const [summary, setSummary] = useState({});    // keyed by source_type: { total_count, approved_count, mapped_count }
    const [destLeaves, setDestLeaves] = useState([]);
    const [destChainData, setDestChainData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadedGroups, setLoadedGroups] = useState({});

    const { register, handleSubmit, control, setValue, formState: { errors } } = useForm();
    const actionForm = useForm();
    const { register: actionRegister, handleSubmit: actionHandleSubmit, control: actionControl, setValue: actionSetValue, reset: actionReset, formState: { errors: actionErrors } } = actionForm;

    // Bulk edit state
    const [bulkEditMode, setBulkEditMode] = useState(false);
    const bulkSelectRef = useRef({});
    const lastClickedIndex = useRef({});
    const sortedTableDataRef = useRef({});
    const [bulkSelectVersion, setBulkSelectVersion] = useState(0);
    const bumpBulkSelect = () => setBulkSelectVersion(v => v + 1);
    const getBulkSet = (resourceId) => bulkSelectRef.current[resourceId] || new Set();
    const getBulkCount = (resourceId) => getBulkSet(resourceId).size;

    // Import log (in-memory, accumulates until page refresh)
    const [importLog, setImportLog] = useState([]);
    const [lastImportSettings, setLastImportSettings] = useState(null);
    const [reloadBanner, setReloadBanner] = useState(null);
    const [mappingFilter, setMappingFilter] = useState('active');
    const [levelFilter, setLevelFilter] = useState('all');

    const [loaded, setLoaded] = useState(false);

    const jobId = jobData?.id;

    // Derive composite mapping entries from jobResourceMappings
    // Also includes sync job's association-type composites (bidirectional mappings shared across paired jobs)
    const [syncCompositeEntries, setSyncCompositeEntries] = useState([]);

    const compositeEntries = useMemo(() => {
        const local = jobResourceMappings?.current?.filter(m => m.mapping_type === 'composite' && m.source_resource_id === 69) || [];
        // Merge with sync job association composites (avoid duplicates by source_resource_id + destination_resource_id)
        const localKeys = new Set(local.map(m => `${m.source_resource_id}-${m.destination_resource_id}`));
        const merged = [...local];
        for (const entry of syncCompositeEntries) {
            if (!localKeys.has(`${entry.source_resource_id}-${entry.destination_resource_id}`)) {
                merged.push(entry);
            }
        }
        return merged;
    }, [jobResourceMappings?.current, syncCompositeEntries]);

    // Fetch sync job's association-type composite mappings (if sync job exists)
    useEffect(() => {
        const syncJobId = jobData?.sync_job_id;
        if (!syncJobId || syncJobId <= 0) return;

        ApiRequest('GET', '/dmt/jobs/{jobId}/mappings', {
            pathParams: { jobId: syncJobId },
        }).then(res => {
            if (!res.success || !res.data) return;
            // Only include composites where at least one chain is association_table type
            const assocComposites = res.data.filter(m => {
                if (m.mapping_type !== 'composite') return false;
                const options = typeof m.mapping_options === 'string'
                    ? JSON.parse(m.mapping_options || '{}')
                    : (m.mapping_options || {});
                const srcType = options?.source_chain?.type;
                const dstType = options?.destination_chain?.type;
                return srcType === 'association_table' || dstType === 'association_table';
            });
            setSyncCompositeEntries(assocComposites);
        });
    }, [jobData?.sync_job_id]);

    // Fetch summary on mount — independent of compositeEntries
    useEffect(() => {
        if (!jobId) return;
        ApiRequest('GET', '/dmt/jobs/{jobId}/compositeMappings', {
            pathParams: { jobId },
            queryParams: { summary: 'true' },
        }).then(res => {
            if (res.success) {
                const data = res.data || [];
                const lookup = {};
                (Array.isArray(data) ? data : []).forEach(s => { lookup[s.source_type] = s; });
                setSummary(lookup);
            }
        });
    }, [jobId]);

    const fetchGroupData = useCallback(async (sourceResourceId) => {
        if (!jobId) return;
        setLoading(true);

        const [mappingsRes, destRes] = await Promise.all([
            ApiRequest('GET', '/dmt/jobs/{jobId}/compositeMappings', {
                pathParams: { jobId },
                queryParams: { sourceResourceId },
            }),
            // Only fetch dest tree once
            destLeaves.length === 0
                ? ApiRequest('GET', '/dmt/jobs/{jobId}/valuemappings/chainOptions', {
                    pathParams: { jobId },
                    queryParams: { sourceResourceId, side: 'destination' },
                })
                : Promise.resolve(null),
        ]);

        if (mappingsRes.success) {
            const data = mappingsRes.data || [];
            const rows = Array.isArray(data) ? data : (data?.records || []);
            setMappings(prev => ({ ...prev, [sourceResourceId]: rows }));
        }

        if (destRes && destRes.success) {
            let data = destRes.data?.records || destRes.data;
            if (typeof data === 'string') try { data = JSON.parse(data); } catch(e) { data = null; }
            if (data) {
                const leaves = [];
                const walk = (nodes, path = []) => {
                    for (const node of (nodes || [])) {
                        const currentPath = [...path, node.value];
                        leaves.push({ ...node, path: currentPath, pathLabel: currentPath.join(' > ') });
                        if (node.children && node.children.length > 0) walk(node.children, currentPath);
                    }
                };
                walk(data.options);
                setDestLeaves(leaves);
                setDestChainData(data);
            }
        }
        setLoading(false);
        setLoadedGroups(prev => ({ ...prev, [sourceResourceId]: true }));
    }, [jobId, destLeaves.length]);


    const handleMappingUpdate = async (formData, mappingId, row) => {
        const destValueField = formData['destinationResourceValueId' + mappingId];
        const statusField = formData['status' + mappingId];
        const notesField = formData['notes' + mappingId];

        const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/{mappingId}', {
            pathParams: { jobId, mappingId: String(mappingId) },
            body: {
                destination_id: destValueField || row.destination_id || 0,
                status: statusField || row.status,
                notes: notesField || null,
            },
        });

        if (res.success) {
            ToastBuilder('success', 'Mapping updated');
            // Refresh the group this mapping belongs to
            const sourceResourceId = compositeEntries.find(e => 
                (mappings[e.source_resource_id] || []).some(m => m.mapping_id === mappingId)
            )?.source_resource_id;
            if (sourceResourceId) fetchGroupData(sourceResourceId);
        } else {
            ToastBuilder('error', res.error || 'Failed to update');
        }
    };

    const handleDeleteMapping = async (mappingId, sourceResourceId) => {
        const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/{mappingId}', {
            pathParams: { jobId, mappingId: String(mappingId) },
            body: { status: 'Delete' },
        });
        if (res.success) {
            ToastBuilder('success', 'Mapping deleted');
            if (sourceResourceId) fetchGroupData(sourceResourceId);
        }
    };

    const handleQuickApprove = async (sourceResourceId) => {
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName: 'quick-approve-composite' },
            body: { sourceResourceId },
        });
        if (res.success) {
            const count = res.data?.approved || 0;
            ToastBuilder('success', `${count} mapping(s) approved`);
        } else {
            ToastBuilder('error', res.data?.message || 'Failed to approve');
        }
        fetchGroupData(sourceResourceId);
    };

    // Bulk edit handlers
    const handleBulkCheckbox = (e, mappingId, resourceId) => {
        const currentData = sortedTableDataRef.current[resourceId] || [];
        const currentIndex = currentData.findIndex(row => row.mapping_id === mappingId);
        const group = new Set(getBulkSet(resourceId));

        if (e.nativeEvent.shiftKey && lastClickedIndex.current[resourceId] !== undefined && currentIndex >= 0) {
            const start = Math.min(lastClickedIndex.current[resourceId], currentIndex);
            const end = Math.max(lastClickedIndex.current[resourceId], currentIndex);
            const rangeIds = currentData.slice(start, end + 1).map(row => row.mapping_id);
            if (e.target.checked) {
                rangeIds.forEach(id => group.add(id));
            } else {
                rangeIds.forEach(id => group.delete(id));
            }
        } else {
            if (e.target.checked) group.add(mappingId); else group.delete(mappingId);
        }

        bulkSelectRef.current = { ...bulkSelectRef.current, [resourceId]: group };
        if (currentIndex >= 0) lastClickedIndex.current = { ...lastClickedIndex.current, [resourceId]: currentIndex };
        bumpBulkSelect();
    };

    const selectAll = (resourceId) => {
        const rows = sortedTableDataRef.current[resourceId] || mappings[resourceId] || [];
        bulkSelectRef.current = { ...bulkSelectRef.current, [resourceId]: new Set(rows.map(r => r.mapping_id)) };
        bumpBulkSelect();
    };

    const deselectAll = (resourceId) => {
        bulkSelectRef.current = { ...bulkSelectRef.current, [resourceId]: new Set() };
        bumpBulkSelect();
    };

    const handleBulkUpdate = async (formData, resourceId) => {
        const selectedIds = [...getBulkSet(resourceId)];
        if (selectedIds.length === 0) return ToastBuilder('error', 'No rows selected');

        const body = { mappingIds: selectedIds };
        if (formData.bulkStatus) body.status = formData.bulkStatus;
        if (formData.bulkDestinationId === 'unset') body.destination_id = -1;
        else if (formData.bulkDestinationId && formData.bulkDestinationId !== '') body.destination_id = parseInt(formData.bulkDestinationId);

        const res = await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/bulkUpdate', {
            pathParams: { jobId },
            body,
        });

        if (res.success && res.data?.success !== false) {
            ToastBuilder('success', `Updated ${res.data?.updated || selectedIds.length} mapping(s)`);
            deselectAll(resourceId);
            fetchGroupData(resourceId);
            actionReset();
        } else {
            ToastBuilder('error', res.data?.message || res.error || 'Failed to bulk update');
        }
    };

    // DMT Action wrapper for components that expect handleDMTAction pattern
    const handleDMTAction = async (actionName, sourceResourceId, callback, formData) => {
        const res = await ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
            pathParams: { jobId, actionName },
            body: { sourceResourceId, formData },
        });
        if (callback) callback(res.success ? res : { data: res.data });
    };

    const handleLoadMappings = async (formData, sourceResourceId) => {

        
        const raw = formData['loadMappingsInput' + sourceResourceId] || '';
        const allowOverwrite = parseInt(formData['loadMappingsOverwrite' + sourceResourceId]) === 1;
        if (!raw.trim()) return ToastBuilder('error', 'No mappings to load');

        const groupRows = mappings[sourceResourceId] || [];
        const lines = raw.trim().split('\n');
        let updated = 0;
        let skipped = [];

        for (const line of lines) {
            const [sourcePart, destPart] = line.split('\t').map(s => (s || '').trim());
            if (!sourcePart || !destPart) { skipped.push(sourcePart || '(empty)'); continue; }

            // Find source mapping by matching source_path
            const sourceMatch = groupRows.find(m =>
                (m.source_path || '').toLowerCase() === sourcePart.toLowerCase()
                || (m.item_name || m.subtype_name || '').toLowerCase() === sourcePart.toLowerCase()
            );

            if (!sourceMatch) { skipped.push(sourcePart); continue; }
            if (!allowOverwrite && sourceMatch.destination_id > 0) { skipped.push(sourcePart + ' (already mapped)'); continue; }

            // Find destination by matching path label
            const destMatch = destLeaves.find(l =>
                l.pathLabel.toLowerCase() === destPart.toLowerCase()
                || l.value?.toLowerCase() === destPart.toLowerCase()
            );

            if (!destMatch) { skipped.push(`${sourcePart} → ${destPart} (dest not found)`); continue; }

            const destId = destMatch.resource_value_id || destMatch.value_id;
            await ApiRequest('PATCH', '/dmt/jobs/{jobId}/compositeMappings/{mappingId}', {
                pathParams: { jobId, mappingId: String(sourceMatch.mapping_id) },
                body: { destination_id: destId, status: 'Pending Approval' },
            });
            updated++;
        }

        if (updated > 0) ToastBuilder('success', `${updated} mapping(s) loaded`);
        if (skipped.length > 0) ToastBuilder('warning', `${skipped.length} skipped: ${skipped.slice(0, 5).join(', ')}${skipped.length > 5 ? '...' : ''}`);
        fetchGroupData(sourceResourceId);
    };

    if (!compositeEntries.length) return null;

    return (
        <Row>
            <Container>
                <Card>
                    <CardHeader>
                        <Row>
                            <Col sm='6'><h4>Composite Value Mapping</h4></Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <div className="default-according" id="accordion">
                            {compositeEntries.map((entry) => {
                                const sourceResourceId = entry.source_resource_id;
                                const accordionId = `composite-${sourceResourceId}`;
                                const isExpanded = isOpen === accordionId;
                                const groupRows = mappings[sourceResourceId] || [];
                                const groupApproved = groupRows.filter(m => m.status === 'Approved').length;
                                const groupMapped = groupRows.filter(m => m.destination_id > 0).length;
                                const groupTotal = groupRows.length;
                                const isGroupLoaded = loadedGroups[sourceResourceId];

                                // Determine source_type for summary lookup
                                const entryOptions = typeof entry.mapping_options === 'string' ? JSON.parse(entry.mapping_options || '{}') : (entry.mapping_options || {});
                                const sourceType = entryOptions.source_chain?.type === 'association_table' ? 'association' : 'resource_value';
                                const summaryData = summary[sourceType];

                                const groupTableData = [...groupRows].sort((a, b) => {
                                    // Mapped first, unmapped last
                                    const aHasDest = a.destination_id > 0;
                                    const bHasDest = b.destination_id > 0;
                                    if (aHasDest && !bHasDest) return -1;
                                    if (!aHasDest && bHasDest) return 1;
                                    // Then by recency
                                    const aUpdated = a.updated_at || 0;
                                    const bUpdated = b.updated_at || 0;
                                    if (aUpdated !== bUpdated) return bUpdated - aUpdated;
                                    // Then alphabetical by source path
                                    const aSource = (a.source_path || '').toLowerCase();
                                    const bSource = (b.source_path || '').toLowerCase();
                                    return aSource.localeCompare(bSource);
                                });

                                const inactiveStatuses = ['Delete', 'Deleted', 'Ignore'];
                                const inactiveCount = groupTableData.filter(m => inactiveStatuses.includes(m.status)).length;
                                const statusFiltered = mappingFilter === 'all' ? groupTableData
                                    : mappingFilter === 'inactive' ? groupTableData.filter(m => inactiveStatuses.includes(m.status))
                                    : groupTableData.filter(m => !inactiveStatuses.includes(m.status));
                                const filteredTableData = levelFilter === 'all' ? statusFiltered
                                    : levelFilter === 'type' ? statusFiltered.filter(m => !m.subtype_name && !m.item_name)
                                    : levelFilter === 'subtype' ? statusFiltered.filter(m => m.subtype_name && !m.item_name)
                                    : statusFiltered.filter(m => !!m.item_name);

                                const handleAccordionToggle = () => {
                                    if (isExpanded) {
                                        setIsOpen(null);
                                        return;
                                    }
                                    setIsOpen(accordionId);
                                    if (!isGroupLoaded) fetchGroupData(sourceResourceId);
                                };

                                return (
                                    <Card key={accordionId}>
                                        <CardHeader className='dmt-accordion-section'>
                                            <center>
                                                <Btn attrBtn={{ as: Card.Header, className: `dmt-accordion-header ${isExpanded ? 'dmt-accordion-open' : ''}`, color: 'default', onClick: handleAccordionToggle }}>
                                                    <span title={`Mapped: ${groupMapped}, Unmapped: ${groupTotal - groupMapped}, Total: ${groupTotal}`}>
                                                        {entry.source_resource_name} <ArrowRight size={14} style={{ verticalAlign: 'middle', margin: '0 4px' }} /> {entry.destination_resource_name} {isGroupLoaded ? `(Approved - ${groupApproved} / ${groupTotal})` : summaryData ? `(Approved - ${summaryData.approved_count} / ${summaryData.total_count})` : ''}
                                                        <div className='text-muted' style={{ fontSize: '10px', lineHeight: 1.2 }}>{sourceResourceId} – {entry.destination_resource_id} (Composite)</div>
                                                    </span>
                                                </Btn>
                                            </center>
                                        </CardHeader>
                                        <Collapse isOpen={isExpanded}>
                                            <CardBody>
                            {reloadBanner === sourceResourceId && (
                                <div className='alert alert-warning d-flex align-items-center justify-content-between py-2 px-3 mb-2' style={{ fontSize: '13px' }}>
                                    <span>⏳ Reload in progress — click <strong>Refresh</strong> when ready to see updated data.</span>
                                    <ButtonBuilder label='Refresh Now' className='btn-info btn-sm m-0 py-1 px-2' onClick={() => { fetchGroupData(sourceResourceId); setReloadBanner(null); }} />
                                </div>
                            )}
                            {loading ? <ProgressLoader /> : (
                                <TableBuilder
                                    subHeader={true}
                                    columnsToSearch={['source_path', 'destination_path', 'destination_value', 'destination_parent_name', 'status', 'board_name', 'type_name', 'subtype_name', 'item_name']}
                                    data={filteredTableData}
                                    sortField={null}
                                    pagination={true}
                                    paginationPerPage={25}
                                    paginationRowsPerPageOptions={[25, 50, 75, 100]}
                                    processing={loading}
                                    onSortChange={(sorted) => { sortedTableDataRef.current = { ...sortedTableDataRef.current, [sourceResourceId]: sorted }; }}
                                    styles={{ cells: { style: { paddingTop: '5px', paddingBottom: '5px' } } }}
                                    title={<><div className='d-flex' style={{ gap: '5px' }}>
                                        {(mappingFilter !== 'active' || inactiveCount > 0) &&
                                        <ButtonBuilder label={
                                            mappingFilter === 'active' ? 'Show All'
                                            : mappingFilter === 'all' ? `Viewing: All ${groupTableData.length}`
                                            : `Viewing: ${inactiveCount} Inactive`
                                        } className={`p-2 px-2 ${mappingFilter === 'active' ? 'btn-primary' : 'btn-info'}`}
                                            style={{ minWidth: '150px' }} useSpinner={false} color={null}
                                            onClick={() => setMappingFilter(mappingFilter === 'active' ? 'all' : mappingFilter === 'all' ? 'inactive' : 'active')}
                                        />}
                                        <ButtonBuilder
                                            label={bulkEditMode ? 'Close Bulk' : 'Bulk Edit'}
                                            className='btn-secondary m-0 px-0 py-2'
                                            style={{ width: '120px' }} useSpinner={false}
                                            onClick={() => { setBulkEditMode(!bulkEditMode); if (bulkEditMode) deselectAll(sourceResourceId); }}
                                        />
                                        {bulkEditMode ? <>
                                            <ButtonBuilder
                                                label={getBulkCount(sourceResourceId) > 0 ? 'Deselect All' : 'Select All'}
                                                className={`${getBulkCount(sourceResourceId) > 0 ? 'btn-info' : 'btn-outline-info'} m-0 px-0 py-2`}
                                                style={{ width: '125px' }}
                                                useSpinner={false}
                                                onClick={() => getBulkCount(sourceResourceId) > 0 ? deselectAll(sourceResourceId) : selectAll(sourceResourceId)}
                                            />
                                        </> : null}
                                        {bulkEditMode && getBulkCount(sourceResourceId) > 0 ?
                                            <ModalButtonBuilder className='btn-danger py-2'
                                                title={`Bulk Edit (${getBulkCount(sourceResourceId)})`}
                                                disabled={getBulkCount(sourceResourceId) === 0}
                                                modalTitle={`Bulk Edit ${entry.source_resource_name} mappings`}
                                                formId={'bulk-update-composite-form-' + sourceResourceId}
                                                body={
                                                    <Form id={'bulk-update-composite-form-' + sourceResourceId} onSubmit={actionHandleSubmit((formData) => handleBulkUpdate(formData, sourceResourceId))}>
                                                        <input type='hidden' {...actionRegister('bulkDestinationId')} />
                                                        <p>Updating <strong>{getBulkCount(sourceResourceId)}</strong> selected mapping(s).</p>
                                                        <p style={{fontSize: 'small', color: 'grey'}}>Fields set to "Do not change" will keep their current value.</p>
                                                        <RegisteredInputsBuilder
                                                            registrator={actionRegister}
                                                            registratorErrors={actionErrors}
                                                            control={actionControl}
                                                            inputs={[
                                                                {
                                                                    label: 'Status', labelSize: 4, inputSize: 8, size: 11, type: 'reactselect',
                                                                    name: 'bulkStatus',
                                                                    options: [{ label: 'Do not change', value: '' }, ...buildSelectOptionsFromArray(['Approved', 'Pending Approval', 'Under Review', 'Ignore', 'Delete'], { labelIsValue: true })],
                                                                },
                                                            ]}
                                                        />
                                                        <div className='row mb-3'>
                                                            <label className='col-3 col-form-label'>Destination</label>
                                                            <div className='col-9'>
                                                                <div className='mb-2'>
                                                                    <small className='text-muted'>Leave unselected to keep current value. Select any level to update.</small>
                                                                </div>
                                                                <CompositeValueSelect
                                                                    jobId={jobId}
                                                                    sourceResourceId={sourceResourceId}
                                                                    value={null}
                                                                    onChange={(val) => actionSetValue('bulkDestinationId', val ? String(val) : '')}
                                                                    chainData={destChainData}
                                                                />
                                                                <div className='mt-1'>
                                                                    <ButtonBuilder
                                                                        label='Unset (clear value)'
                                                                        className='btn-outline-secondary py-1 px-2'
                                                                        style={{ fontSize: '11px' }}
                                                                        useSpinner={false}
                                                                        type='button'
                                                                        onClick={() => actionSetValue('bulkDestinationId', 'unset')}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </Form>
                                                }
                                            /> : null}
                                    </div></>}
                                    actions={<Fragment>
                                        <select
                                            className='form-select form-select-sm m-1'
                                            style={{ width: '130px', height: '38px', display: 'inline-block' }}
                                            value={levelFilter}
                                            onChange={(e) => setLevelFilter(e.target.value)}
                                        >
                                            <option value='all'>All Levels</option>
                                            <option value='type'>Type Only</option>
                                            <option value='subtype'>SubType</option>
                                            <option value='item'>Item</option>
                                        </select>
                                        <ButtonBuilder label='Refresh' className='btn-info m-1 py-2' onClick={() => fetchGroupData(sourceResourceId)} />
                                        <ButtonBuilder label='Reload TSI' className='btn-secondary m-1 py-2' onClick={() => {
                                            ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                pathParams: { jobId, actionName: 'reload-tsi' },
                                                body: { sourceResourceId },
                                            }).then(() => {
                                                setReloadBanner(sourceResourceId);
                                                setTimeout(() => setReloadBanner(null), 30000);
                                            });
                                        }} />
                                        <ButtonBuilder label='Reload All' className='btn-warning m-1 py-2' onClick={() => {
                                            ApiRequest('POST', '/dmt/jobs/{jobId}/actions/{actionName}', {
                                                pathParams: { jobId, actionName: 'reload-composite-dependencies' },
                                                body: { sourceResourceId },
                                            }).then(() => {
                                                setReloadBanner(sourceResourceId);
                                                setTimeout(() => setReloadBanner(null), 30000);
                                            });
                                        }} />
                                        {importLog.length > 0 && (
                                            <ModalButtonBuilder title={`Log (${importLog.length})`} className='btn-warning m-1 py-2'
                                                onSubmit={false} onCancelLabel='Close'
                                                modalTitle='Import Log'
                                                style={{ minWidth: '600px' }}
                                                body={
                                                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                        {importLog.map((entry, idx) => (
                                                            <div key={idx} className='mb-3 p-2 border rounded' style={{ fontSize: '12px' }}>
                                                                <div className='d-flex justify-content-between mb-1'>
                                                                    <strong>{entry.message}</strong>
                                                                    <small className='text-muted'>{moment(entry.timestamp).fromNow()}</small>
                                                                </div>
                                                                {entry.skipped && entry.skipped.length > 0 && (
                                                                    <>
                                                                        <div className='d-flex justify-content-end mb-1'>
                                                                            <ButtonBuilder
                                                                                label='Copy Failed Rows'
                                                                                className='btn-outline-secondary py-1 px-2'
                                                                                style={{ fontSize: '11px' }}
                                                                                useSpinner={false}
                                                                                onClick={() => {
                                                                                    const rawRows = entry.rawRows || [];
                                                                                    const failedLines = entry.skipped.map(s => {
                                                                                        const rowIdx = (s.row || 1) - 1;
                                                                                        return rawRows[rowIdx] ? rawRows[rowIdx].join('\t') : `Row ${s.row}\t${s.reason}`;
                                                                                    });
                                                                                    handleCopy(failedLines.join('\n'));
                                                                                    ToastBuilder('success', 'Failed rows copied to clipboard');
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        <table className='table table-sm mb-0' style={{ fontSize: '11px' }}>
                                                                            <thead><tr><th style={{width: 50}}>Row</th><th>Reason</th></tr></thead>
                                                                            <tbody>
                                                                                {entry.skipped.map((s, i) => (
                                                                                    <tr key={i}><td>{s.row}</td><td>{s.reason}</td></tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    </>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                }
                                            />
                                        )}
                                        <VerticalButtonGroup label='Actions' className='btn-danger m-1 py-2'
                                            style={{ minWidth: '140px' }}
                                            buttons={[
                                                <ModalButtonBuilder title='Quick Approve' className='btn-outline-danger'
                                                    formId={'quick-approve-composite-form-' + sourceResourceId}
                                                    body={
                                                        <Form id={'quick-approve-composite-form-' + sourceResourceId} onSubmit={handleSubmit(() => handleQuickApprove(sourceResourceId))}>
                                                            <p>Set status to "Approved" for all mapped composite mappings that are not yet approved.</p>
                                                        </Form>
                                                    }
                                                />,
                                                <ModalButtonBuilder title='Load Mappings' className='btn-outline-danger'
                                                    formId={'load-composite-mappings-form-' + sourceResourceId}
                                                    modalTitle={`Load ${entry.source_resource_name} Mappings`}
                                                    onClick={() => { actionSetValue('loadMappingsInput' + sourceResourceId, ''); actionSetValue('loadMappingsOverwrite' + sourceResourceId, 0); }}
                                                    body={
                                                        <Form id={'load-composite-mappings-form-' + sourceResourceId} onSubmit={actionHandleSubmit((formData) => handleLoadMappings(formData, sourceResourceId))}>
                                                            <p>Paste tab-separated mappings: <b>Source Path → Destination Path</b></p>
                                                            <ul style={{ fontSize: '12px', color: '#636E72' }}>
                                                                <li>Source can be the full path or just the leaf value name</li>
                                                                <li>Destination can be the full path or leaf value name</li>
                                                                <li>Unrecognized values will be skipped</li>
                                                            </ul>
                                                            <RegisteredInputsBuilder
                                                                registrator={actionRegister}
                                                                registratorErrors={actionErrors}
                                                                control={actionControl}
                                                                inputs={[
                                                                    {
                                                                        label: 'Overwrite', offset: 1, labelSize: 2, type: 'reactselect',
                                                                        name: 'loadMappingsOverwrite' + sourceResourceId,
                                                                        options: [{ label: 'Only update unmapped rows', value: 0 }, { label: 'Allow overwriting existing', value: 1 }],
                                                                    },
                                                                    {
                                                                        label: 'Mappings', offset: 1, inputSize: 10, type: 'textarea',
                                                                        name: 'loadMappingsInput' + sourceResourceId,
                                                                        placeholder: 'Incident > Security > 1 - MFA Tools\tSecurity\n1 - Password Reset\tAccount & Access > Login / Password Reset',
                                                                        style: { height: '200px', fontFamily: 'monospace', fontSize: '12px' },
                                                                        settings: { handleTabs: true, resize: true },
                                                                    },
                                                                ]}
                                                            />
                                                        </Form>
                                                    }
                                                />,
                                                <ImportAssociationMappingsModal
                                                    sourceResourceId={sourceResourceId}
                                                    sourceResourceName={entry.source_resource_name}
                                                    actionRegister={actionRegister}
                                                    actionErrors={actionErrors}
                                                    actionControl={actionControl}
                                                    actionSetValue={actionSetValue}
                                                    actionReset={actionReset}
                                                    handleDMTAction={handleDMTAction}
                                                    getResourceValueMappings={() => fetchGroupData(sourceResourceId)}
                                                    onImportResult={(result) => setImportLog(prev => [{ ...result, timestamp: Date.now(), sourceResourceId }, ...prev])}
                                                    lastImportSettings={lastImportSettings}
                                                    setLastImportSettings={setLastImportSettings}
                                                />,
                                            ]}
                                        />
                                    </Fragment>}
                                    columns={[
                                        {
                                            name: '',
                                            selector: (row) => <CheckBox className="medium-checkbox-cursor-pointer"
                                                id={`composite-checkbox-${row.mapping_id}`}
                                                reference={bulkSelectRef.current[sourceResourceId] || new Set()}
                                                checked={getBulkSet(sourceResourceId).has(row.mapping_id)}
                                                onChange={(e) => handleBulkCheckbox(e, row.mapping_id, sourceResourceId)}
                                            />,
                                            sortable: false,
                                            center: true,
                                            width: '70px',
                                            grow: 0,
                                            omit: !bulkEditMode,
                                        },
                                        {
                                            name: 'Source (CWM)',
                                            selector: (row) => <LabelWithSubtext
                                                text={row.item_name || row.subtype_name || row.type_name}
                                                subtext={row.source_type === 'association'
                                                    ? row.item_name
                                                        ? `${row.board_name} > ${row.type_name} > ${row.subtype_name}`
                                                        : row.subtype_name
                                                            ? `${row.board_name} > ${row.type_name}`
                                                            : row.board_name || ''
                                                    : row.board_name || ''
                                                }
                                                align='center'
                                            />,
                                            sortable: true,
                                            center: true,
                                            sortFunction: (a, b) => sortByKey(a, b, 'source_path'),
                                        },
                                        {
                                            name: 'Destination (FS)',
                                            selector: (row) => {
                                                if (!row.destination_value) return '-';
                                                // Build subtext from destination_path, excluding the leaf value itself
                                                let subtext = '';
                                                if (row.destination_path) {
                                                    const parts = row.destination_path.split(' > ');
                                                    if (parts.length > 1) {
                                                        subtext = parts.slice(0, -1).join(' > ');
                                                    }
                                                } else {
                                                    subtext = row.destination_parent_name || '';
                                                }
                                                return <LabelWithSubtext
                                                    text={row.destination_value}
                                                    subtext={subtext}
                                                    align='center'
                                                />;
                                            },
                                            sortable: true,
                                            center: true,
                                            sortFunction: (a, b) => sortByKey(a, b, 'destination_value'),
                                        },
                                        {
                                            name: 'Status',
                                            selector: (row) => row.status || '-',
                                            sortable: true,
                                            center: true,
                                            maxWidth: '13%',
                                        },
                                        {
                                            name: 'Notes',
                                            selector: (row) => row.notes ? <HoverOver content={row.notes} text={'VIEW'} /> : '',
                                            sortable: false,
                                            center: true,
                                            maxWidth: '7%',
                                        },
                                        {
                                            name: 'Updated',
                                            selector: (row) => row.updated_at ? moment.utc(row.updated_at * 1000).fromNow() : '-',
                                            sortable: true,
                                            center: true,
                                            maxWidth: '11%',
                                            sortFunction: (a, b) => (a.updated_at || 0) - (b.updated_at || 0),
                                        },
                                        {
                                            name: 'Manage',
                                            selector: (row) => (
                                                <ModalButtonBuilder className='btn-secondary'
                                                    title='Edit'
                                                    onClick={() => {
                                                        setValue('sourceDisplay' + row.mapping_id, row.source_path || `${row.subtype_name}${row.item_name ? ' > ' + row.item_name : ''}`);
                                                        setValue('destinationResourceValueId' + row.mapping_id, row.destination_id > 0 ? row.destination_id : '');
                                                        setValue('status' + row.mapping_id, row.status || 'Pending Approval');
                                                        setValue('notes' + row.mapping_id, row.notes || '');
                                                    }}
                                                    formId={'edit-assoc-mapping-form' + row.mapping_id}
                                                    body={
                                                        <Form id={'edit-assoc-mapping-form' + row.mapping_id} onSubmit={handleSubmit(data => handleMappingUpdate(data, row.mapping_id, row))}>
                                                            <input type='hidden' {...register('destinationResourceValueId' + row.mapping_id)} />
                                                            <RegisteredInputsBuilder
                                                                control={control}
                                                                registrator={register}
                                                                registratorErrors={errors}
                                                                inputs={[
                                                                    {
                                                                        label: 'Source',
                                                                        labelSize: 3,
                                                                        name: 'sourceDisplay' + row.mapping_id,
                                                                        type: 'static',
                                                                        defaultValue: row.source_path,
                                                                    },
                                                                ]}
                                                            />
                                                            <div className='row mb-3'>
                                                                <label className='col-3 col-form-label'>Destination</label>
                                                                <div className='col-9'>
                                                                    <CompositeValueSelect
                                                                        jobId={jobId}
                                                                        sourceResourceId={sourceResourceId}
                                                                        value={row.destination_id > 0 ? row.destination_id : null}
                                                                        onChange={(val) => setValue('destinationResourceValueId' + row.mapping_id, val)}
                                                                        chainData={destChainData}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <RegisteredInputsBuilder
                                                                control={control}
                                                                registrator={register}
                                                                registratorErrors={errors}
                                                                inputs={[
                                                                    {
                                                                        label: 'Status',
                                                                        labelSize: 3,
                                                                        type: 'reactselect',
                                                                        name: 'status' + row.mapping_id,
                                                                        options: buildOptionsFromArray(['Approved', 'Pending Approval', 'Under Review', 'Ignore', 'Delete'], { labelIsValue: true }),
                                                                    },
                                                                    {
                                                                        label: 'Notes',
                                                                        labelSize: 3,
                                                                        name: 'notes' + row.mapping_id,
                                                                        type: 'textarea',
                                                                        settings: { handleTabs: true, resize: true },
                                                                    },
                                                                ]}
                                                            />
                                                        </Form>
                                                    }
                                                />
                                            ),
                                            sortable: false,
                                            center: true,
                                            width: '140px',
                                            grow: 0,
                                        },
                                    ]}
                                />
                            )}
                        </CardBody>
                    </Collapse>
                </Card>
                );
            })}
            </div>
            </CardBody>
                </Card>
            </Container>
        </Row>
    );
};

export default AssociationMappingSection;
